import type { App, UpdateAppInput } from '@keboola/api-client/developerPortal/types';

import type {
  AllowedProcessorPosition,
  Complexity,
  ComponentTypeOrOther,
  DataTypeSupport,
  DefaultBucketStage,
  Logger,
  RepositoryType,
  StagingStorageInput,
  StagingStorageOutput,
} from '../vocabulary';

/** The audience a form is rendered for: decides which fields exist and which are writable. */
export type Audience = 'vendor' | 'admin';

/**
 * The form's value shape.
 *
 * - Every field exists for both audiences and is never `null` / `undefined` (controlled inputs).
 * - Admin-only fields are dropped by `toUpdatePayload`, so a vendor cannot send `isPublic`.
 * - JSON fields hold the editor's raw text, not parsed values.
 */
export type EditAppFormValues = {
  name: string;
  type: ComponentTypeOrOther | '';
  shortDescription: string;
  longDescription: string;
  licenseUrl: string;
  documentationUrl: string;
  sourceCodeUrl: string;
  repository: { type: RepositoryType | ''; uri: string; tag: string };
  defaultBucket: boolean;
  defaultBucketStage: DefaultBucketStage;
  uiOptions: string[];
  imageParameters: string;
  stackParameters: string;
  configurationSchema: string;
  configurationRowSchema: string;
  emptyConfiguration: string;
  emptyConfigurationRow: string;
  createConfigurationRowSchema: string;
  configurationDescription: string;
  actions: string[];
  fees: boolean;
  limits: string;
  logger: Logger;
  loggerConfiguration: string;
  stagingStorageInput: StagingStorageInput;
  stagingStorageOutput: StagingStorageOutput;
  dataTypeSupport: DataTypeSupport | '';
  allowedProcessorPosition: AllowedProcessorPosition | '';
  // Admin-only from here down.
  isPublic: boolean;
  isDeprecated: boolean;
  forwardToken: boolean;
  forwardTokenDetails: boolean;
  requiredMemory: string;
  processTimeout: string;
  permissions: string;
  features: string[];
  complexity: Complexity | '';
  categories: string[];
};

/** Fields only an admin may write. Everything else is writable by both audiences. */
export const ADMIN_ONLY_FIELDS = [
  'isPublic',
  'isDeprecated',
  'forwardToken',
  'forwardTokenDetails',
  'requiredMemory',
  'processTimeout',
  'permissions',
  'features',
  'complexity',
  'categories',
  // Admin-only: a vendor never sees these fields.
  'dataTypeSupport',
  'allowedProcessorPosition',
] as const satisfies readonly (keyof EditAppFormValues)[];

/**
 * The fields a data app does not have.
 *
 * - ⚠️ One list, three consumers: the JSX guard, the schema omit and the payload omit.
 * - `loggerConfiguration` is deliberately absent: a data app with gelf configured keeps it.
 */
export const DATA_APP_HIDDEN_FIELDS = [
  'defaultBucket',
  'defaultBucketStage',
  'configurationSchema',
  'configurationRowSchema',
  'emptyConfiguration',
  'emptyConfigurationRow',
  'createConfigurationRowSchema',
  'configurationDescription',
  'actions',
  'limits',
  'fees',
  'stagingStorageInput',
  'stagingStorageOutput',
  'logger',
] as const satisfies readonly (keyof EditAppFormValues)[];

/** Whether a component of this type has the fields listed in `DATA_APP_HIDDEN_FIELDS`. */
export const hasConfigurationFields = (type: EditAppFormValues['type']): boolean =>
  type !== 'data-app';

/** JSON-object text fields. Empty text means "no value", which goes on the wire as `null`. */
const JSON_OBJECT_FIELDS = [
  'imageParameters',
  'stackParameters',
  'configurationSchema',
  'configurationRowSchema',
  'emptyConfiguration',
  'emptyConfigurationRow',
  'createConfigurationRowSchema',
  'loggerConfiguration',
] as const satisfies readonly (keyof EditAppFormValues)[];

/** `permissions` is the one JSON field holding an array rather than an object. */
const JSON_ARRAY_FIELDS = ['permissions'] as const satisfies readonly (keyof EditAppFormValues)[];

/**
 * Text fields the API stores as nullable, where empty means "cleared".
 *
 * - Sent as `null`; `''` would render as an empty link on the public detail page.
 */
const NULLABLE_TEXT_FIELDS = [
  'licenseUrl',
  'documentationUrl',
  'sourceCodeUrl',
  'configurationDescription',
] as const satisfies readonly (keyof EditAppFormValues)[];

/** Pretty-prints a stored JSON value back into editor text. `null` / absent becomes empty. */
const toJsonText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return JSON.stringify(value, null, 2);
};

/**
 * Projects a stored component onto the form's value shape.
 *
 * - Server-owned fields (`vendor`, `version`, `icon`, audit fields) have no key here.
 * - Every `null` maps to the empty value its control needs.
 */
export const toFormValues = (app: App): EditAppFormValues => ({
  name: app.name,
  type: app.type,
  shortDescription: app.shortDescription ?? '',
  longDescription: app.longDescription ?? '',
  licenseUrl: app.licenseUrl ?? '',
  documentationUrl: app.documentationUrl ?? '',
  sourceCodeUrl: app.sourceCodeUrl ?? '',
  repository: {
    type: app.repository?.type ?? '',
    uri: app.repository?.uri ?? '',
    tag: app.repository?.tag ?? '',
  },
  defaultBucket: app.defaultBucket ?? false,
  defaultBucketStage: app.defaultBucketStage ?? 'in',
  uiOptions: app.uiOptions ?? [],
  imageParameters: toJsonText(app.imageParameters),
  stackParameters: toJsonText(app.stackParameters),
  configurationSchema: toJsonText(app.configurationSchema),
  configurationRowSchema: toJsonText(app.configurationRowSchema),
  emptyConfiguration: toJsonText(app.emptyConfiguration),
  emptyConfigurationRow: toJsonText(app.emptyConfigurationRow),
  createConfigurationRowSchema: toJsonText(app.createConfigurationRowSchema),
  configurationDescription: app.configurationDescription ?? '',
  actions: app.actions ?? [],
  fees: app.fees ?? false,
  limits: app.limits ?? '',
  // apps-api's own defaults, so an untouched form rewrites nothing.
  logger: app.logger ?? 'standard',
  loggerConfiguration: toJsonText(app.loggerConfiguration),
  stagingStorageInput: app.stagingStorageInput ?? 'local',
  stagingStorageOutput: app.stagingStorageOutput ?? 'local',
  dataTypeSupport: app.dataTypeSupport ?? '',
  allowedProcessorPosition: app.allowedProcessorPosition ?? '',
  isPublic: app.isPublic ?? false,
  isDeprecated: app.isDeprecated ?? false,
  forwardToken: app.forwardToken ?? false,
  forwardTokenDetails: app.forwardTokenDetails ?? false,
  requiredMemory: app.requiredMemory ?? '',
  processTimeout:
    app.processTimeout === undefined || app.processTimeout === null
      ? ''
      : String(app.processTimeout),
  permissions: toJsonText(app.permissions),
  features: app.features ?? [],
  complexity: app.complexity ?? '',
  categories: app.categories ?? [],
});

/**
 * Parses JSON editor text for the wire; empty is `null`.
 *
 * - The schema already validated the text, so a throw here is a bug, not user input.
 */
const parseJsonField = (text: string): unknown => {
  if (text.trim() === '') return null;
  return JSON.parse(text);
};

/**
 * Builds the `PATCH` payload from form values.
 *
 * - Only fields the audience may write; empty enum selections are omitted (apps-api rejects `''`).
 * - Fields not rendered for this type are omitted, so `PATCH` keeps them as stored.
 * - ⚠️ Omit before parsing JSON: a hidden field was never validated and may not parse.
 * - ⚠️ `UpdateAppInput` admits any key; only `values.test.ts` pins the exact key set.
 */
export const toUpdatePayload = (values: EditAppFormValues, audience: Audience): UpdateAppInput => {
  const omitted = new Set<string>([
    ...(audience === 'vendor' ? ADMIN_ONLY_FIELDS : []),
    ...(hasConfigurationFields(values.type) ? [] : DATA_APP_HIDDEN_FIELDS),
  ]);

  const payload: Record<string, unknown> = {
    name: values.name,
    type: values.type,
    shortDescription: values.shortDescription,
    longDescription: values.longDescription,
    repository: {
      ...(values.repository.type === '' ? {} : { type: values.repository.type }),
      uri: values.repository.uri,
      tag: values.repository.tag,
    },
    defaultBucket: values.defaultBucket,
    defaultBucketStage: values.defaultBucketStage,
    uiOptions: values.uiOptions,
    actions: values.actions,
    fees: values.fees,
    limits: values.limits,
    logger: values.logger,
    stagingStorageInput: values.stagingStorageInput,
    stagingStorageOutput: values.stagingStorageOutput,
    isPublic: values.isPublic,
    isDeprecated: values.isDeprecated,
    forwardToken: values.forwardToken,
    forwardTokenDetails: values.forwardTokenDetails,
    requiredMemory: values.requiredMemory,
    features: values.features,
    complexity: values.complexity,
    categories: values.categories,
    dataTypeSupport: values.dataTypeSupport,
    allowedProcessorPosition: values.allowedProcessorPosition,
  };

  for (const field of omitted) delete payload[field];

  for (const field of NULLABLE_TEXT_FIELDS) {
    if (omitted.has(field)) continue;
    payload[field] = values[field] === '' ? null : values[field];
  }
  for (const field of [...JSON_OBJECT_FIELDS, ...JSON_ARRAY_FIELDS]) {
    if (omitted.has(field)) continue;
    payload[field] = parseJsonField(values[field]);
  }

  // A blank timeout means "use the platform default", which apps-api expresses as `null`.
  if (!omitted.has('processTimeout')) {
    payload.processTimeout = values.processTimeout === '' ? null : Number(values.processTimeout);
  }

  // Drop empty enum selections last, so nothing above resurrects a key as `''`.
  for (const field of ['type', 'complexity', 'dataTypeSupport', 'allowedProcessorPosition']) {
    if (payload[field] === '') delete payload[field];
  }

  return payload;
};
